import {
  ArrowRight as ArrowRightIcon,
  CheckCircle as CheckCircleIcon,
  MagnifyingGlass as MagnifyingGlassIcon,
  Users as UsersIcon,
  Warning as WarningIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRbac } from '@/hooks/use-rbac';
import { BulkUpdateRequest } from '@/types/sheets';

import {
  calculatePermissionChanges,
  filterUsersBySearch,
  formatUserSelectionSummary,
  getBulkUpdateErrorMessage,
  getRoleColorScheme,
  getRoleDisplayName,
  getRolePermissionCount,
  getUserRoleName,
  groupUsersByCurrentRole,
  sortUsersByName,
  validateBulkOperation,
} from './shared/rbac-utils';

interface BulkPermissionUpdateProps {
  users: any[];
  roles: any[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface UserUpdateConfig {
  userId: string;
  user: any;
  selected: boolean;
}

const BulkPermissionUpdate: React.FC<BulkPermissionUpdateProps> = ({
  users,
  roles,
  onSuccess,
  onCancel,
}) => {
  const { bulkUpdateUserPermissions, loading } = useRbac();

  // State management
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userConfigs, setUserConfigs] = useState<UserUpdateConfig[]>(
    users.map(user => ({
      userId: user._id,
      user,
      selected: false,
    }))
  );
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Memoized computed values
  const filteredUsers = useMemo(() => {
    const filtered = filterUsersBySearch(users as any, searchTerm);
    return sortUsersByName(filtered);
  }, [users, searchTerm]);

  const filteredUserConfigs = useMemo(() => {
    return userConfigs.filter(config =>
      filteredUsers.some(user => user._id === config.userId)
    );
  }, [userConfigs, filteredUsers]);

  const selectedUsers = useMemo(
    () => userConfigs.filter(config => config.selected),
    [userConfigs]
  );

  const selectedRole = useMemo(
    () => roles.find(role => role._id === selectedRoleId),
    [roles, selectedRoleId]
  );

  const rolesByCurrentRole = useMemo(
    () => groupUsersByCurrentRole(selectedUsers.map(c => c.user) as any),
    [selectedUsers]
  );

  const permissionChanges = useMemo(() => {
    if (!selectedRole || selectedUsers.length === 0) {
      return { totalChanges: 0, permissionsAdded: 0, permissionsRemoved: 0 };
    }
    return calculatePermissionChanges(
      selectedUsers.map(c => c.user) as any,
      selectedRole as any
    );
  }, [selectedUsers, selectedRole]);

  // Event handlers
  const handleUserSelection = useCallback(
    (userId: string, selected: boolean) => {
      setUserConfigs(prev =>
        prev.map(config =>
          config.userId === userId ? { ...config, selected } : config
        )
      );
      setFormErrors([]); // Clear errors when selection changes
    },
    []
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      setUserConfigs(prev =>
        prev.map(config => {
          // Only select users that are currently visible (filtered)
          const isVisible = filteredUsers.some(
            user => user._id === config.userId
          );
          return {
            ...config,
            selected: isVisible ? selected : config.selected,
          };
        })
      );
      setFormErrors([]);
    },
    [filteredUsers]
  );

  const handleRoleSelection = useCallback((roleId: string) => {
    setSelectedRoleId(roleId);
    setFormErrors([]);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleBulkUpdate = useCallback(async () => {
    // Clear previous errors
    setFormErrors([]);

    // Validate the operation
    const validation = validateBulkOperation(
      selectedUsers.map(c => c.user),
      selectedRoleId
    );

    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    const updateRequest: BulkUpdateRequest = {
      updates: selectedUsers.map(config => ({
        userId: config.userId,
        roleId: selectedRoleId,
      })),
    };

    try {
      const result = await bulkUpdateUserPermissions(updateRequest);

      if (result.success) {
        // Close the sheet on success
        if (onSuccess) {
          await onSuccess();
        }
      } else {
        setFormErrors([
          'Bulk update failed. Some users may not have been updated.',
        ]);
      }
    } catch (error) {
      const errorMessage = getBulkUpdateErrorMessage(
        error,
        'Failed to update user permissions. Please try again.'
      );
      setFormErrors([errorMessage]);
    }
  }, [selectedUsers, selectedRoleId, bulkUpdateUserPermissions, onSuccess]);

  // Helper functions
  const getCurrentRoleName = useCallback(
    (user: any) => getUserRoleName(user as any),
    []
  );

  const getSelectionSummary = () => {
    const visible = filteredUserConfigs.length;
    const total = userConfigs.length;
    const selected = selectedUsers.length;

    if (searchTerm) {
      return `${selected} selected of ${visible} filtered (${total} total)`;
    }
    return `${selected} of ${total} selected`;
  };

  // Expose handlers for dynamic button system
  React.useEffect(() => {
    const validation = validateBulkOperation(
      selectedUsers.map(c => c.user),
      selectedRoleId
    );

    (
      window as typeof window & {
        bulkPermissionUpdateHandlers?: {
          handleBulkUpdate: () => void;
          handleCancel?: () => void;
          isLoading: boolean;
          isDisabled: boolean;
          selectedUsersCount: number;
        };
      }
    ).bulkPermissionUpdateHandlers = {
      handleBulkUpdate,
      handleCancel: onCancel,
      isLoading: loading.operations,
      isDisabled: !validation.isValid || loading.operations,
      selectedUsersCount: selectedUsers.length,
    };

    return () => {
      delete (
        window as typeof window & { bulkPermissionUpdateHandlers?: unknown }
      ).bulkPermissionUpdateHandlers;
    };
  }, [
    handleBulkUpdate,
    onCancel,
    loading.operations,
    selectedUsers.length,
    selectedRoleId,
    // Removed selectedUsers to prevent infinite loop - length is sufficient
  ]);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-start justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-gray-90'>
            Bulk Permission Update
          </h2>
          <p className='text-sm text-gray-60 mt-1'>
            Assign roles and update permissions for multiple users
            simultaneously
          </p>
        </div>
        <Badge variant='outline' className='text-sm'>
          {formatUserSelectionSummary(selectedUsers.map(c => c.user))}
        </Badge>
      </div>

      {/* Form errors display */}
      {formErrors.length > 0 && (
        <div className='p-4 bg-destructive-5 border border-destructive-20 rounded-lg'>
          <div className='flex items-start gap-3'>
            <WarningIcon className='h-5 w-5 text-destructive-50 flex-shrink-0 mt-0.5' />
            <div className='flex-1'>
              <h3 className='font-medium text-destructive-90 text-sm mb-1'>
                Cannot proceed with bulk update:
              </h3>
              <ul className='text-sm text-destructive-70 space-y-1'>
                {formErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Role Selection */}
      <Card className='p-6'>
        <h3 className='font-semibold text-gray-90 mb-4'>1. Select New Role</h3>
        <Select value={selectedRoleId} onValueChange={handleRoleSelection}>
          <SelectTrigger className='w-full'>
            <SelectValue placeholder='Choose a role to assign to selected users...' />
          </SelectTrigger>
          <SelectContent>
            {roles.map(role => {
              const colorScheme = getRoleColorScheme(role.name);
              return (
                <SelectItem key={role._id} value={role._id}>
                  <div className='flex items-center justify-between w-full min-w-0'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className='font-medium truncate'>
                        {getRoleDisplayName(role)}
                      </span>
                      <Badge
                        variant='outline'
                        className={`text-xs ${colorScheme.text} ${colorScheme.bg}`}
                      >
                        {getRolePermissionCount(role)} permissions
                      </Badge>
                    </div>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {selectedRole && (
          <div className='mt-4 p-4 bg-brand-5 border border-brand-20 rounded-lg'>
            <div className='flex items-start gap-3'>
              <CheckCircleIcon className='h-5 w-5 text-brand-60 flex-shrink-0 mt-0.5' />
              <div className='flex-1 min-w-0'>
                <h4 className='font-medium text-brand-90 text-sm mb-1'>
                  Selected Role: {selectedRole.name}
                </h4>
                <p className='text-xs text-brand-70'>
                  This role has {getRolePermissionCount(selectedRole)} total
                  permissions
                  {selectedRole.description && ` • ${selectedRole.description}`}
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* User Selection */}
      <Card className='p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='font-semibold text-gray-90'>
            2. Select Users to Update
          </h3>
          <div className='text-sm text-gray-60'>{getSelectionSummary()}</div>
        </div>

        {/* Search and Select All */}
        <div className='flex items-center gap-4 mb-4'>
          <div className='relative flex-1'>
            <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-40' />
            <Input
              placeholder='Search users by name, email, or role...'
              value={searchTerm}
              onChange={e => handleSearchChange(e.target.value)}
              className='pl-10'
            />
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox
              checked={
                filteredUserConfigs.length > 0 &&
                filteredUserConfigs.every(config => config.selected)
              }
              onCheckedChange={handleSelectAll}
              disabled={filteredUserConfigs.length === 0}
              className={
                filteredUserConfigs.some(config => config.selected) &&
                !filteredUserConfigs.every(config => config.selected)
                  ? 'opacity-70'
                  : ''
              }
            />
            <span className='text-sm text-gray-60'>Select All Filtered</span>
          </div>
        </div>

        {/* User List */}
        <div className='space-y-2 max-h-80 overflow-y-auto border rounded-lg'>
          {filteredUserConfigs.length === 0 ? (
            <div className='p-8 text-center text-gray-50'>
              {searchTerm
                ? 'No users match your search criteria'
                : 'No users available'}
            </div>
          ) : (
            filteredUserConfigs.map(config => {
              const currentRoleName = getCurrentRoleName(config.user);
              const currentRoleScheme = getRoleColorScheme(currentRoleName);
              const newRoleScheme = selectedRole
                ? getRoleColorScheme(selectedRole.name)
                : null;

              return (
                <div
                  key={config.userId}
                  className={`flex items-center justify-between p-4 border-b last:border-b-0 transition-colors ${
                    config.selected ? 'bg-brand-5' : 'hover:bg-gray-5'
                  }`}
                >
                  <div className='flex items-center gap-3 min-w-0 flex-1'>
                    <Checkbox
                      checked={config.selected}
                      onCheckedChange={checked =>
                        handleUserSelection(config.userId, checked as boolean)
                      }
                    />
                    <div className='flex items-center gap-2 min-w-0'>
                      <UsersIcon className='h-4 w-4 text-gray-40 flex-shrink-0' />
                      <div className='min-w-0 flex-1'>
                        <p className='font-medium text-gray-90 truncate'>
                          {config.user.username}
                        </p>
                        <p className='text-sm text-gray-60 truncate'>
                          {config.user.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='flex items-center gap-2 flex-shrink-0'>
                    <Badge
                      variant='outline'
                      className={`text-xs ${currentRoleScheme.text} ${currentRoleScheme.bg}`}
                    >
                      {currentRoleName}
                    </Badge>
                    {config.selected && selectedRole && (
                      <>
                        <ArrowRightIcon className='h-4 w-4 text-gray-40' />
                        <Badge
                          className={`text-xs ${newRoleScheme?.text} ${newRoleScheme?.bg} border-0`}
                        >
                          {selectedRole.name}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Change Summary */}
      {selectedUsers.length > 0 && selectedRole && (
        <Card className='p-6 bg-amber-50 border-amber-200'>
          <div className='flex items-start gap-3'>
            <WarningIcon className='h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5' />
            <div className='flex-1'>
              <h4 className='font-semibold text-amber-900 mb-2'>
                Review Changes Before Proceeding
              </h4>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
                <div className='text-center p-3 bg-white rounded-lg border border-amber-200'>
                  <div className='text-2xl font-bold text-amber-900'>
                    {selectedUsers.length}
                  </div>
                  <div className='text-xs text-amber-700'>Users Affected</div>
                </div>
                <div className='text-center p-3 bg-white rounded-lg border border-amber-200'>
                  <div className='text-2xl font-bold text-success-70'>
                    +{permissionChanges.permissionsAdded}
                  </div>
                  <div className='text-xs text-amber-700'>
                    Permissions Added
                  </div>
                </div>
                <div className='text-center p-3 bg-white rounded-lg border border-amber-200'>
                  <div className='text-2xl font-bold text-destructive-70'>
                    -{permissionChanges.permissionsRemoved}
                  </div>
                  <div className='text-xs text-amber-700'>
                    Permissions Removed
                  </div>
                </div>
              </div>

              {Object.entries(rolesByCurrentRole).length > 1 && (
                <div className='mb-4'>
                  <p className='text-sm text-amber-800 font-medium mb-2'>
                    Users by Current Role:
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    {Object.entries(rolesByCurrentRole).map(([role, users]) => (
                      <Badge key={role} variant='outline' className='text-xs'>
                        {role}: {users.length}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <p className='text-sm text-amber-800'>
                <strong>Important:</strong> This action will immediately update
                all selected users to the <strong>{selectedRole.name}</strong>{' '}
                role. Users will receive new permissions and lose access to any
                permissions not included in the new role.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default BulkPermissionUpdate;
